import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon,
  PlusCircleIcon,
  MinusCircleIcon,
  ClockIcon,
  XMarkIcon,
  CheckCircleIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';

interface Wallet {
  wallet_id: number;
  user_id: number;
  user_name: string;
  user_email: string;
  user_phone: string;
  user_role: string;
  balance: number;
  currency: string;
  created_at: string;
  updated_at: string | null;
}

interface Transaction {
  id: number;
  transaction_type: string;
  amount: number;
  description: string;
  balance_after: number | null;
  reference: string;
  order_id: number | null;
  created_at: string;
}

const WalletManagement: React.FC = () => {
  // Wallet list state
  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  const [selectedWallet, setSelectedWallet] = useState<Wallet | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [showDebitModal, setShowDebitModal] = useState(false);
  const [showConfirmDebit, setShowConfirmDebit] = useState(false);
  
  const [creditAmount, setCreditAmount] = useState('');
  const [creditDescription, setCreditDescription] = useState('');
  const [debitAmount, setDebitAmount] = useState('');
  const [debitDescription, setDebitDescription] = useState('');
  const [processing, setProcessing] = useState(false);
  const [modalError, setModalError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  // Transaction pagination
  const [transactionPage, setTransactionPage] = useState(1);
  const [totalTransactions, setTotalTransactions] = useState(0);
  const [totalTransactionPages, setTotalTransactionPages] = useState(1);

  // Fetch wallets
  const fetchWallets = async (page: number = 1, search: string = '') => {
    try {
      setIsLoading(true);
      setError('');
      const response = await api.get('/admin/wallets', {
        params: {
          page,
          limit: 20,
          search: search || undefined,
        },
      });
      setWallets(response.data.wallets);
      setTotal(response.data.total);
      setTotalPages(response.data.pages);
      setCurrentPage(page);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load wallets');
    } finally {
      setIsLoading(false);
    }
  };

  // Load wallets on mount and when search changes
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchWallets(1, searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const goToPage = (page: number) => {
    fetchWallets(page, searchTerm);
  };

  const refresh = () => {
    fetchWallets(currentPage, searchTerm);
  };

  const fetchTransactions = async (walletId: number, page: number = 1) => {
    try {
      setLoadingTransactions(true);
      const response = await api.get(`/admin/wallets/${walletId}/transactions?page=${page}&limit=50`);
      setTransactions(response.data.transactions);
      setTotalTransactions(response.data.total);
      setTotalTransactionPages(response.data.pages);
      setTransactionPage(page);
    } catch (error: any) {
      console.error('Error fetching transactions:', error);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleViewWallet = (wallet: Wallet) => {
    setSelectedWallet(wallet);
    setTransactionPage(1);
    fetchTransactions(wallet.wallet_id, 1);
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 5000);
  };

  const handleCreditWallet = async () => {
    if (!selectedWallet) return;
    
    setModalError('');

    const amount = parseFloat(creditAmount);
    if (isNaN(amount) || amount <= 0) {
      setModalError('Please enter a valid amount greater than 0');
      return;
    }

    if (!creditDescription.trim()) {
      setModalError('Please provide a description');
      return;
    }

    setProcessing(true);

    try {

      await api.post(`/admin/wallets/${selectedWallet.wallet_id}/credit`, null, {
        params: {
          amount,
          description: creditDescription,
        },
      });

      showSuccess(`Successfully credited ₦${amount.toLocaleString()} to ${selectedWallet.user_name}'s wallet`);
      setShowCreditModal(false);
      setCreditAmount('');
      setCreditDescription('');
      
      // Update selected wallet balance in modal
      setSelectedWallet({
        ...selectedWallet,
        balance: selectedWallet.balance + amount,
        updated_at: new Date().toISOString()
      });
      
      refresh();
      fetchTransactions(selectedWallet.wallet_id, transactionPage);
    } catch (error: any) {
      setModalError(error.response?.data?.detail || 'Failed to credit wallet');
    } finally {
      setProcessing(false);
    }
  };

  const handleInitiateDebit = () => {
    const amount = parseFloat(debitAmount);
    if (isNaN(amount) || amount <= 0) {
      setModalError('Please enter a valid amount greater than 0');
      return;
    }

    if (!debitDescription.trim()) {
      setModalError('Please provide a description');
      return;
    }

    setShowConfirmDebit(true);
    setModalError('');
  };

  const handleConfirmDebit = async () => {
    if (!selectedWallet) return;
    
    setProcessing(true);
    setModalError('');

    try {
      const amount = parseFloat(debitAmount);

      await api.post(`/admin/wallets/${selectedWallet.wallet_id}/debit`, null, {
        params: {
          amount,
          description: debitDescription,
        },
      });

      showSuccess(`Successfully debited ₦${amount.toLocaleString()} from ${selectedWallet.user_name}'s wallet`);
      setShowDebitModal(false);
      setShowConfirmDebit(false);
      setDebitAmount('');
      setDebitDescription('');
      
      // Update selected wallet balance in modal
      setSelectedWallet({
        ...selectedWallet,
        balance: selectedWallet.balance - amount,
        updated_at: new Date().toISOString()
      });
      
      refresh();
      fetchTransactions(selectedWallet.wallet_id, transactionPage);
    } catch (error: any) {
      setModalError(error.response?.data?.detail || 'Failed to debit wallet');
      setShowConfirmDebit(false);
    } finally {
      setProcessing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Wallet Management</h1>
        <p className="text-gray-600">Manage user wallets, view transactions, and perform manual adjustments</p>
      </div>

      {/* Search */}
      <div className="mb-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 text-red-800">
          <p>{error}</p>
        </div>
      )}

      {/* Wallets Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Balance
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Last Updated
                </th>
                <th className="px-6 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500"></div>
                    </div>
                  </td>
                </tr>
              ) : wallets.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center text-gray-500">
                    {searchTerm ? 'No wallets found matching your search' : 'No wallets found'}
                  </td>
                </tr>
              ) : (
                wallets.map((wallet) => (
                  <tr key={wallet.wallet_id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-semibold text-gray-900">{wallet.user_name}</div>
                        <div className="text-sm text-gray-500">ID: {wallet.user_id}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">
                        <div className="text-gray-900">{wallet.user_email}</div>
                        <div className="text-gray-500">{wallet.user_phone}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        wallet.user_role === 'admin'
                          ? 'bg-purple-100 text-purple-800'
                          : wallet.user_role === 'rider'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {wallet.user_role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-lg font-bold text-gray-900">
                        {wallet.currency} {wallet.balance.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {wallet.updated_at ? formatDate(wallet.updated_at) : 'Never'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleViewWallet(wallet)}
                        className="text-sky-600 hover:text-sky-800 font-medium"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              Showing {wallets.length} of {total} wallets
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <span className="px-4 py-1 text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Wallet Details Modal */}
      {selectedWallet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">{selectedWallet.user_name}'s Wallet</h2>
                <p className="text-sm text-gray-600">{selectedWallet.user_email}</p>
              </div>
              <button
                onClick={() => setSelectedWallet(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Balance & Actions */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Current Balance</p>
                  <p className="text-3xl font-bold text-gray-900">
                    {selectedWallet.currency} {selectedWallet.balance.toLocaleString()}
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowCreditModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <PlusCircleIcon className="h-5 w-5" />
                    Credit Wallet
                  </button>
                  <button
                    onClick={() => setShowDebitModal(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                  >
                    <MinusCircleIcon className="h-5 w-5" />
                    Debit Wallet
                  </button>
                </div>
              </div>
            </div>

            {/* Transaction History */}
            <div className="px-6 py-4 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 280px)' }}>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Transaction History</h3>
              
              {loadingTransactions ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-500 mx-auto"></div>
                </div>
              ) : transactions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No transactions yet
                </div>
              ) : (
                <div className="space-y-3">
                  {transactions.map((tx) => (
                    <div
                      key={tx.id}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-full ${
                          tx.transaction_type === 'credit'
                            ? 'bg-green-100 text-green-600'
                            : 'bg-red-100 text-red-600'
                        }`}>
                          {tx.transaction_type === 'credit' ? (
                            <PlusCircleIcon className="h-5 w-5" />
                          ) : (
                            <MinusCircleIcon className="h-5 w-5" />
                          )}
                        </div>
                        <div>
                          <p className="font-semibold text-gray-900">{tx.description}</p>
                          <div className="flex gap-4 text-sm text-gray-500 mt-1">
                            <span className="flex items-center gap-1">
                              <ClockIcon className="h-4 w-4" />
                              {formatDate(tx.created_at)}
                            </span>
                            {tx.reference && (
                              <span className="font-mono text-xs">Ref: {tx.reference}</span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`text-lg font-bold ${
                          tx.transaction_type === 'credit' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {tx.transaction_type === 'credit' ? '+' : '-'}₦{tx.amount.toLocaleString()}
                        </p>
                        {tx.balance_after !== null && (
                          <p className="text-sm text-gray-500">Balance: ₦{tx.balance_after.toLocaleString()}</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Transaction Pagination */}
              {!loadingTransactions && totalTransactionPages > 1 && (
                <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
                  <div className="text-sm text-gray-600">
                    Showing page {transactionPage} of {totalTransactionPages} ({totalTransactions} total transactions)
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => selectedWallet && fetchTransactions(selectedWallet.wallet_id, transactionPage - 1)}
                      disabled={transactionPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      <ChevronLeftIcon className="h-4 w-4" />
                      Previous
                    </button>
                    <button
                      onClick={() => selectedWallet && fetchTransactions(selectedWallet.wallet_id, transactionPage + 1)}
                      disabled={transactionPage === totalTransactionPages}
                      className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
                    >
                      Next
                      <ChevronRightIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Credit Modal */}
      {showCreditModal && selectedWallet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Credit Wallet</h3>
            
            {modalError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                {modalError}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount ({selectedWallet.currency})
                </label>
                <input
                  type="number"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description/Reason
                </label>
                <textarea
                  value={creditDescription}
                  onChange={(e) => setCreditDescription(e.target.value)}
                  placeholder="Enter reason for credit"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowCreditModal(false);
                    setCreditAmount('');
                    setCreditDescription('');
                    setModalError('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  disabled={processing}
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreditWallet}
                  disabled={processing}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                >
                  {processing ? 'Processing...' : 'Credit Wallet'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Debit Modal */}
      {showDebitModal && selectedWallet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[60]">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Debit Wallet</h3>
            
            {modalError && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-800 text-sm">
                {modalError}
              </div>
            )}

            <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                Current balance: {selectedWallet.currency} {selectedWallet.balance.toLocaleString()}
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount ({selectedWallet.currency})
                </label>
                <input
                  type="number"
                  value={debitAmount}
                  onChange={(e) => setDebitAmount(e.target.value)}
                  placeholder="Enter amount"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description/Reason
                </label>
                <textarea
                  value={debitDescription}
                  onChange={(e) => setDebitDescription(e.target.value)}
                  placeholder="Enter reason for debit"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-sky-500 focus:border-transparent"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => {
                    setShowDebitModal(false);
                    setDebitAmount('');
                    setDebitDescription('');
                    setModalError('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  disabled={processing}
                >
                  Cancel
                </button>
                <button
                  onClick={handleInitiateDebit}
                  disabled={processing}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Debit Confirmation Modal */}
      {showConfirmDebit && selectedWallet && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[70]">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-red-600 mb-4">Confirm Debit Operation</h3>
            
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-gray-700 mb-2">
                You are about to <span className="font-semibold text-red-600">debit</span> the following amount:
              </p>
              <div className="mt-3 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">User:</span>
                  <span className="font-semibold">{selectedWallet.user_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Amount:</span>
                  <span className="font-semibold text-red-600">₦{parseFloat(debitAmount).toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Current Balance:</span>
                  <span className="font-semibold">₦{selectedWallet.balance.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">New Balance:</span>
                  <span className="font-semibold">₦{(selectedWallet.balance - parseFloat(debitAmount)).toLocaleString()}</span>
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-600 mb-6">
              This action cannot be undone. Please confirm that you want to proceed with this debit operation.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowConfirmDebit(false);
                  setModalError('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                disabled={processing}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDebit}
                disabled={processing}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-semibold"
              >
                {processing ? 'Processing...' : 'Confirm Debit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Toast */}
      {successMessage && (
        <div className="fixed bottom-4 right-4 bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg z-[80] flex items-center gap-3 animate-slide-in">
          <CheckCircleIcon className="h-6 w-6" />
          <p className="font-medium">{successMessage}</p>
        </div>
      )}
    </div>
  );
};

export default WalletManagement;
